// File: components/dashboard/HeroBanner.tsx
"use client";

import { useEffect, useState } from "react";
import { Gem, Eye, Quote, ChevronRight } from "lucide-react";
import HardNavLink from "@/components/shared/HardNavLink";
import type { AppUser } from "@/components/layout/AppShell";
import type { Quote as QuoteType, Stats24h } from "@/actions/stats";

// ═════════════════════════════════════════════════════════════
// HELPERS (greeting, badge) — dùng nội bộ
// ═════════════════════════════════════════════════════════════

function getGreeting(name: string): { text: string; emoji: string } {
  const hour = new Date().getHours();
  const firstName = name.split(" ").pop() || name;

  if (hour >= 0 && hour < 12) return { text: `Chào buổi sáng, ${firstName}`, emoji: "☀️" };
  if (hour >= 12 && hour < 18) return { text: `Chào buổi chiều, ${firstName}`, emoji: "🌤️" };
  return { text: `Chào buổi tối, ${firstName}`, emoji: "🌙" };
}

function CategoryBadge({ category }: { category: string }) {
  const cfg: Record<string, string> = {
    motivation: "bg-orange-50 text-orange-700 border-orange-200",
    learning:   "bg-teal-50 text-teal-700 border-teal-200",
    prosperity: "bg-amber-50 text-amber-700 border-amber-200",
    effort:     "bg-blue-50 text-blue-700 border-blue-200",
    wisdom:     "bg-violet-50 text-violet-700 border-violet-200",
  };
  const labelVi: Record<string, string> = {
    motivation: "Động lực",
    learning:   "Học tập",
    prosperity: "Thịnh vượng",
    effort:     "Nỗ lực",
    wisdom:     "Trí tuệ",
  };
  return (
    <span className={`inline-flex items-center px-2 py-[2px] rounded-full text-[9px] font-bold border ${cfg[category] ?? "bg-muted text-muted-foreground border-border"}`}>
      {labelVi[category] ?? category}
    </span>
  );
}

// ═════════════════════════════════════════════════════════════
// StatCard (single) — dùng bởi HeroStats3Cards
// ═════════════════════════════════════════════════════════════

function StatCard({
  icon: Icon, label, value, gradient, iconColor,
  footnote = "24 giờ qua",
  detailHref,
}: {
  icon:        typeof Gem;
  label:       string;
  value:       number;
  gradient:    string;
  iconColor:   string;
  footnote?:   string;
  detailHref?: string;
}) {
  const [displayed, setDisplayed] = useState(0);

  useEffect(() => {
    if (value === 0) { setDisplayed(0); return; }
    let start = 0;
    const step = Math.ceil(value / 20);
    const timer = setInterval(() => {
      start = Math.min(start + step, value);
      setDisplayed(start);
      if (start >= value) clearInterval(timer);
    }, 30);
    return () => clearInterval(timer);
  }, [value]);

  return (
    <div className={`relative overflow-hidden rounded-2xl p-4 ${gradient}`}>
      <div className="absolute top-0 right-0 w-20 h-20 rounded-full bg-white/10 -translate-y-1/2 translate-x-1/2 pointer-events-none" />
      <div className="relative">
        <div className="flex items-start justify-between mb-2.5">
          <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
            <Icon className={`w-4.5 h-4.5 ${iconColor}`} />
          </div>
          {detailHref && (
            <HardNavLink
              href={detailHref}
              className="inline-flex items-center gap-0.5 text-[10px] font-bold text-white/80 hover:text-white bg-white/15 hover:bg-white/25 px-2 py-1 rounded-lg transition-colors"
            >
              Chi tiết
              <ChevronRight className="w-3 h-3" />
            </HardNavLink>
          )}
        </div>
        <div className="text-[22px] font-extrabold text-white leading-none mb-0.5 tabular-nums">
          {displayed.toLocaleString("vi-VN")}
        </div>
        <div className="text-[11px] text-white/70 font-medium">{label}</div>
        <div className="text-[9px] text-white/40 mt-0.5">{footnote}</div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// HeroBannerLeft — Greeting + Quote (cột trái 2/3)
// Render ngay không chờ stats.
// ═════════════════════════════════════════════════════════════

export function HeroBannerLeft({ user, quote }: { user: AppUser | null; quote: QuoteType | null }) {
  const [showVi, setShowVi] = useState(true);
  const [copied, setCopied] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => { setHydrated(true); }, []);

  const displayName = user?.full_name || user?.username || "bạn";
  const greeting = hydrated ? getGreeting(displayName) : { text: `Xin chào, ${displayName}`, emoji: "👋" };

  function copyQuote() {
    if (!quote) return;
    const text = `"${showVi ? quote.content_vn : quote.content_en}" — ${quote.author}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="lg:col-span-2 relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0f3a4a] via-[#1a5570] to-[#2a7a9a] p-6 min-h-[172px]">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(249,115,22,.15),transparent_55%)] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/[.03] rounded-full translate-y-1/2 -translate-x-1/4 pointer-events-none" />

      <div className="relative h-full flex flex-col justify-between gap-4">
        <div>
          <p className="text-white/50 text-[12px] font-medium mb-0.5">
            {new Date().toLocaleDateString("vi-VN", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" })}
          </p>
          <h2 className="font-display text-[20px] sm:text-[22px] font-extrabold text-white leading-tight">
            {greeting.text} {greeting.emoji}
          </h2>
        </div>

        {quote ? (
          <div className="flex-1 flex flex-col justify-end">
            <div className="flex items-start gap-2.5">
              <Quote className="w-4 h-4 text-orange-400 shrink-0 mt-1" />
              <div className="flex-1 min-w-0">
                <p
                  className="text-white/85 text-[13px] leading-relaxed font-medium cursor-pointer transition-opacity hover:text-white"
                  onClick={() => setShowVi(v => !v)}
                  title="Nhấn để đổi ngôn ngữ"
                >
                  {showVi ? quote.content_vn : quote.content_en}
                </p>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <span className="text-[11px] text-white/40 font-medium">— {quote.author}</span>
                  <CategoryBadge category={quote.category} />
                  <button
                    onClick={() => setShowVi(v => !v)}
                    className="text-[9px] text-white/30 hover:text-white/60 transition-colors font-medium border border-white/15 px-1.5 py-0.5 rounded"
                  >
                    {showVi ? "EN" : "VI"}
                  </button>
                  <button
                    onClick={copyQuote}
                    className="text-[9px] text-white/30 hover:text-orange-400 transition-colors font-medium border border-white/15 px-1.5 py-0.5 rounded flex items-center gap-1"
                  >
                    {copied ? "✓ Đã copy" : "Copy"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-end">
            <p className="text-white/30 text-[12px] italic">Đang tải câu nói truyền cảm hứng...</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// HeroStats3Cards — 3 stat card (cột phải 1/3)
// Server hoặc client component đều render được.
// `loggedIn` để biết có phủ lock overlay không.
// ═════════════════════════════════════════════════════════════

export function HeroStats3Cards({ stats, loggedIn }: { stats: Stats24h; loggedIn: boolean }) {
  return (
    <div className="relative flex flex-col gap-3">
      {!loggedIn && (
        <div className="absolute inset-0 backdrop-blur-[2px] bg-background/50 flex flex-col items-center justify-center z-10 rounded-2xl">
          <span className="text-2xl mb-1">🔒</span>
          <p className="text-[11px] font-bold text-muted-foreground">Đăng nhập để xem</p>
        </div>
      )}

      {/* 1. Kim cương XANH nhận trong 24h — có nút Chi tiết → /studio/revenue */}
      <StatCard
        icon={Gem}
        label="Kim cương nhận được"
        value={stats.diamonds24h}
        gradient="bg-gradient-to-br from-amber-500 to-orange-600"
        iconColor="text-white"
        footnote="24 giờ qua"
        detailHref="/studio/revenue"
      />

      {/* 2. Kim cương HỒNG nhận trong 24h */}
      <StatCard
        icon={Gem}
        label="Kim cương hồng nhận được"
        value={stats.pinkDiamonds24h}
        gradient="bg-gradient-to-br from-rose-500 to-pink-600"
        iconColor="text-white"
        footnote="24 giờ qua"
      />

      {/* 3. Tổng lượt xem cumulative */}
      <StatCard
        icon={Eye}
        label="Lượt xem"
        value={stats.views}
        gradient="bg-gradient-to-br from-teal-600 to-teal-800"
        iconColor="text-white"
        footnote="Tổng cộng"
      />
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// HeroBanner (default export) — wrapper full grid 2 cột
// Giữ lại cho component nào cần render hero hoàn chỉnh trong 1 lần.
// HomePage.tsx vẫn dùng cái này khi !hideHero.
// Route home (`app/(main)/page.tsx`) stream stats riêng nên không dùng default export.
// ═════════════════════════════════════════════════════════════

interface Props {
  user:  AppUser | null;
  quote: QuoteType | null;
  stats: Stats24h;
}

export default function HeroBanner({ user, quote, stats }: Props) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
      <HeroBannerLeft user={user} quote={quote} />
      <HeroStats3Cards stats={stats} loggedIn={!!user} />
    </div>
  );
}
